/**
 * Prazos Context - Gerenciamento de estado dos prazos
 */
import { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react'
import ApiService from '@services/api'
import { APP_CONFIG } from '@config/constants'
import { createPrazoHubConnection } from '@services/signalr'
import { isPrazoOpen } from '@services/prazoStatus'
import { excludeRedundantNatashaPrazos } from '@services/prazoDeduplication'

export const PrazosContext = createContext(null)

const KANBAN_STATUS_SCOPE = 'open'
const DEBUG_PRAZOS = import.meta.env.DEV

function debugPrazos(...args) {
  if (DEBUG_PRAZOS) console.debug(...args)
}

function extractPrazosList(result) {
  if (!result) return null
  if (Array.isArray(result)) return result
  if (Array.isArray(result.items)) return result.items

  const payload = result.data
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results

  if (result.success === false) return null

  return null
}

function getPrazoRealtimeEventNames() {
  return Array.from(new Set([
    APP_CONFIG.SIGNALR_PRAZOS_EVENT,
    'prazosAtualizados',
    'PrazosAtualizados',
  ].filter(Boolean)))
}

export function PrazosProvider({ children }) {
  const [prazos, setPrazos] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [realtimeStatus, setRealtimeStatus] = useState('disconnected')
  const [activePrazosConsumers, setActivePrazosConsumers] = useState(0)
  const hubConnectionRef = useRef(null)

  const applyPrazos = useCallback((items) => {
    setPrazos(excludeRedundantNatashaPrazos(Array.isArray(items) ? items.filter(isPrazoOpen) : []))
    setLoading(false)
  }, [])

  const syncPrazosWithResult = useCallback((result) => {
    const list = extractPrazosList(result)
    if (!Array.isArray(list)) return false

    setPrazos(excludeRedundantNatashaPrazos(list.filter(isPrazoOpen)))
    setLoading(false)
    return true
  }, [])

  const upsertPrazo = useCallback((prazo) => {
    if (!prazo?.id) return
    setPrazos((current) => {
      const withoutCurrent = current.filter((item) => item.id !== prazo.id)
      return excludeRedundantNatashaPrazos(isPrazoOpen(prazo) ? [...withoutCurrent, prazo] : withoutCurrent)
    })
  }, [])

  const applyRealtimeBatch = useCallback((payload) => {
    const upserted = Array.isArray(payload?.upserted) ? payload.upserted : []
    const removedIds = new Set(Array.isArray(payload?.removedIds) ? payload.removedIds.map(String) : [])

    setPrazos((current) => {
      const byId = new Map(current.map((prazo) => [String(prazo.id), prazo]))
      removedIds.forEach((id) => byId.delete(id))
      upserted.forEach((prazo) => {
        if (!prazo?.id) return
        if (isPrazoOpen(prazo)) byId.set(String(prazo.id), prazo)
        else byId.delete(String(prazo.id))
      })
      return excludeRedundantNatashaPrazos(Array.from(byId.values()))
    })
  }, [])

  const loadPrazos = useCallback(async ({ silent = false } = {}) => {
    const filters = { limit: 10000, page: 1, statusScope: KANBAN_STATUS_SCOPE }
    debugPrazos('Fetching prazos', { queryKey: ['prazos-board', filters.statusScope, filters.page, filters.limit], filters })
    if (!silent) setLoading(true)
    setError(null)
    try {
      const result = await ApiService.getPrazos(filters)
      const list = extractPrazosList(result)
      
      if (Array.isArray(list)) {
        setPrazos(excludeRedundantNatashaPrazos(list.filter(isPrazoOpen)))
      } else if (result.success) {
        setPrazos([])
      } else {
        setError(result.error || 'Erro ao carregar prazos')
      }
    } catch (err) {
      console.error('Erro ao carregar prazos:', err)
      setError('Erro ao conectar com o servidor')
    } finally {
      setLoading(false)
    }
  }, [])

  const mountPrazosFeature = useCallback(() => {
    debugPrazos('Prazos query mounted')
    setActivePrazosConsumers((current) => current + 1)

    return () => {
      debugPrazos('Prazos query unmounted')
      setActivePrazosConsumers((current) => Math.max(0, current - 1))
    }
  }, [])

  const addPrazo = useCallback(async (data) => {
    const result = await ApiService.createPrazo(data)
    if (result.success) upsertPrazo(result.data)
    return result
  }, [upsertPrazo])

  const updatePrazo = useCallback(async (id, data) => {
    const result = await ApiService.patchPrazo(id, data)
    if (result.success) upsertPrazo(result.data)
    return result
  }, [upsertPrazo])

  const deletePrazo = useCallback(async (id) => {
    const result = await ApiService.deletePrazo(id)
    if (result.success) {
      setPrazos((prev) => prev.filter((p) => String(p.id) !== String(id)))
    }
    return result
  }, [])

  const importPrazos = useCallback(async (prazosArray) => {
    const result = await ApiService.importPrazos(prazosArray)
    return {
      imported: result.success ? Number(result.data?.sucesso || 0) : 0,
      total: prazosArray.length,
      error: result.success ? null : result.error,
    }
  }, [])

  // Diagnóstico de performance: o problema estava neste provider quando ele era montado
  // globalmente em App.jsx; isso disparava loadPrazos + SignalR em qualquer tela autenticada.
  // Agora a feature precisa chamar usePrazosBoardData(), e só o Kanban ativa o carregamento.

  // Realtime híbrido: snapshot autoritativo ao conectar/reconectar e deltas durante a sessão.
  useEffect(() => {
    if (activePrazosConsumers <= 0) {
      debugPrazos('Skipping prazos fetch: feature not mounted')
      return undefined
    }

    if (!APP_CONFIG.SIGNALR_ENABLED) {
      setRealtimeStatus('fallback')
      loadPrazos()
      return undefined
    }

    setLoading(true)
    setRealtimeStatus('connecting')
    let cancelled = false

    const handleRealtimePrazos = (result) => {
      debugPrazos('SignalR prazos event received')
      setRealtimeStatus('connected')

      const updated = syncPrazosWithResult(result)
      if (!updated) {
        debugPrazos('Invalidating active prazos queries', { reason: 'signalr-payload-missing-list' })
        loadPrazos({ silent: true })
      }
    }

    const handleUpsert = (prazo) => upsertPrazo(prazo)
    const handleRemoved = (id) => setPrazos((current) => current.filter((prazo) => String(prazo.id) !== String(id)))
    const handleBatch = (payload) => applyRealtimeBatch(payload)

    const startConnection = async () => {
      try {
        const tokenFactory = () => ApiService.getAuthToken() || ''
        const connection = await createPrazoHubConnection(tokenFactory)
        if (cancelled) return

        hubConnectionRef.current = connection

        Array.from([...getPrazoRealtimeEventNames(), 'PrazosSnapshot']).forEach((eventName) => {
          connection.on(eventName, handleRealtimePrazos)
        })
        connection.on('PrazoUpserted', handleUpsert)
        connection.on('PrazoRemoved', handleRemoved)
        connection.on('PrazosBatchChanged', handleBatch)

        const subscribeAndReconcile = async () => {
          const snapshot = await connection.invoke('SubscreverBuscarPrazos', {
            page: 1,
            limit: 10000,
            statusScope: KANBAN_STATUS_SCOPE,
          })
          handleRealtimePrazos(snapshot)
        }

        connection.onreconnected(() => {
          setRealtimeStatus('connecting')
          subscribeAndReconcile().catch((err) => {
            console.warn('Não foi possível renovar a inscrição SignalR de prazos. Usando API como fallback.', err)
            setRealtimeStatus('fallback')
            loadPrazos({ silent: true })
          })
        })

        connection.onreconnecting(() => {
          setRealtimeStatus('reconnecting')
        })

        connection.onclose(() => {
          setRealtimeStatus('fallback')
          if (!cancelled) loadPrazos({ silent: true })
        })

        await connection.start()
        if (!cancelled) {
          await subscribeAndReconcile()
        }
      } catch (err) {
        console.warn('SignalR indisponível. Carregando prazos pela API.', err)
        setRealtimeStatus('fallback')
        loadPrazos()
      }
    }

    startConnection()

    return () => {
      cancelled = true
      const connection = hubConnectionRef.current
      if (connection) {
        Array.from([...getPrazoRealtimeEventNames(), 'PrazosSnapshot']).forEach((eventName) => {
          connection.off(eventName, handleRealtimePrazos)
        })
        connection.off('PrazoUpserted', handleUpsert)
        connection.off('PrazoRemoved', handleRemoved)
        connection.off('PrazosBatchChanged', handleBatch)
        connection.stop()
      }
      hubConnectionRef.current = null
    }
  }, [activePrazosConsumers, applyRealtimeBatch, loadPrazos, syncPrazosWithResult, upsertPrazo])

  const value = {
    prazos,
    loading,
    error,
    loadPrazos,
    mountPrazosFeature,
    addPrazo,
    updatePrazo,
    deletePrazo,
    importPrazos,
    applyPrazos,
    syncPrazosWithResult,
    realtimeStatus,
    realtimeConnected: realtimeStatus === 'connected',
  }

  return (
    <PrazosContext.Provider value={value}>
      {children}
    </PrazosContext.Provider>
  )
}


export function usePrazosBoardData() {
  const context = usePrazos()

  useEffect(() => {
    return context.mountPrazosFeature()
  }, [context.mountPrazosFeature])

  return context
}

/**
 * Hook customizado para acessar o contexto de prazos
 */
export function usePrazos() {
  const context = useContext(PrazosContext)
  if (!context) {
    throw new Error('usePrazos must be used within a PrazosProvider')
  }
  return context
}
